import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, forwardRef, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AnimationOption, MatchingItem, QuestionType } from '../../../common/models';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

@Component({
    selector: 'lib-question-matching',
    templateUrl: './question-matching.component.html',
    styleUrls: ['./question-matching.component.scss'],
    animations: [
        trigger('itemAnimation', [
            transition(':leave', animate('300ms ease-in-out', keyframes([
                style({
                    offset: 0.2,
                    opacity: 0.9,
                    transform: 'translateY(100%)'
                }),
                style({
                    offset: 0.5,
                    opacity: 0.6,
                    transform: 'translateY(150%)'
                }),
                style({
                    offset: 0.8,
                    opacity: 0.2,
                    transform: 'translateY(200%)'
                }),
                style({
                    offset: 1,
                    height: 0,
                    padding: 0,
                    opacity: 0,
                    transform: 'translateY(300%)'
                }),
            ])))
        ]),
        trigger('matchAnimation', [
            transition(':enter', animate('200ms ease-in', keyframes([
                style({
                    offset: 0,
                    opacity: 0,
                    transform: 'translateY(-300%)'
                }),
                style({
                    offset: 0.5,
                    opacity: .2,
                    transform: 'translateY(-200%)'
                }),
                style({
                    offset: 0.8,
                    opacity: .6,
                    transform: 'translateY(-150%)'
                }),
                style({
                    offset: 1,
                    opacity: 1,
                    transform: 'translateY(-100%)'
                }),
            ])))
        ])
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuestionMatchingComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QuestionMatchingComponent implements ControlValueAccessor {
  @Input() animation: AnimationOption = AnimationOption.NONE;
  @Input() animationDelay = 0;
  @Input() interactions = true;
  @Input() canEdit = false;
  @Input() disableAnimations = false;
  @Input() isTranslation = false;
  @Input() showError = false;

  @Input() set items(value: { questions: MatchingItem[], answers: MatchingItem[]}) {
    if (!value) {
      return;
    }

    this.questions = value.questions?.map((item, i) => ({ ...item, index: i })) || [];
    this.answers = value.answers?.map((item, i) => ({ ...item, index: i })) || [];
    this.matchingLength = this.questions?.length;
  }

  @Output() edit: EventEmitter<number> = new EventEmitter();
  @Output() remove: EventEmitter<number> = new EventEmitter();
  @Output() public orderChanged: EventEmitter<{previousIndex: number, currentIndex: number}> = new EventEmitter();

  public questions: MatchingItem[] = [];
  public answers: MatchingItem[] = [];

  public disabled: boolean;

  public output: { question: MatchingItem, answer: MatchingItem }[] = [];

  public activeOption: MatchingItem = null;
  public activeAnswer: MatchingItem = null;

  private isLoading = false;

  private matchingLength = 0;

  onChange = (data: any) => {};
  onTouched = () => {};


  constructor() { }

  trackByFn(_index: number, item: MatchingItem) {
    return item.hash;
  }

  public markOptionAsActive(item: MatchingItem): void {
    if (this.isLoading || !this.interactions) {
      return;
    }

    this.activeOption = item;
    this.match();
  }

  public markAnswerAsActive(item: MatchingItem): void {
    if (this.isLoading || !this.interactions) {
      return;
    }

    this.activeAnswer = item;
    this.match();
  }

  public detachMatching(index: number): void {
    const match = this.output[index];

    if (!match) {
      return;
    }

    this.questions.push(match.question);
    this.answers.push(match.answer);

    this.output.splice(index, 1);
    this.matchingChanged();
  }

  public onDrop(event: CdkDragDrop<MatchingItem[]>): void {
    this.orderChanged.emit({ previousIndex: event.previousIndex, currentIndex: event.currentIndex});
  }

  private match(): void {
    if (!this.activeOption || !this.activeAnswer) {
      return;
    }

    const question = { ...this.activeOption };
    const answer = { ...this.activeAnswer };

    this.isLoading = true;

    setTimeout(() => {
      this.output.push({ question, answer });

      const questionIndex = this.questions.findIndex(item => item.hash === question.hash);
      const answerIndex = this.answers.findIndex(item => item.hash === answer.hash);

      this.answers.splice(answerIndex, 1);
      this.questions.splice(questionIndex, 1);

      this.activeOption = null;
      this.activeAnswer = null;
      this.matchingChanged();
      this.isLoading = false;
    }, 200);
  }

  private matchingChanged(): void {
    const count = this.output?.length;

     if (count !== this.matchingLength) {
      this.onChange(null);
      return;
    }

    const result = {
      type: QuestionType.MATCHING_QUESTION,
      matchingItems: this.output.map(match => ({
        questionHash: match.question.hash,
        answersHash: match.answer.hash
      }))
    };

    this.onChange(result);
  }

  public writeValue(obj: any): void {}

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
